/**
 * FILE: CreatePollModal.js
 * PURPOSE: Full-screen 3-step wizard for creating polls.
 *
 * DESIGN ARCHITECTURE:
 *  - Left panel  : 3-step wizard (progressive disclosure)
 *  - Right panel : Live poll preview (endowment effect)
 *
 * STEPS:
 *  1 — The Idea      : Question + Poll Type + Category
 *  2 — The Rules     : Options + Duration
 *  3 — Audience      : VANE Qualifying Criteria + Visibility + Launch
 *
 * PSYCHOLOGICAL HOOKS:
 *  - Sunk-cost progress bar
 *  - Endowment-effect live preview
 *  - Typing momentum indicator (border pulse)
 *  - Social proof micro-copy
 *  - Scarcity framing on duration
 *  - "Who is this for?" framing on gating
 *  - Golden-path defaults (24h, Tech, Binary)
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, X, ShieldAlert, Globe, AlertCircle, BarChart2, List,
  Activity, CheckSquare, Image as ImageIcon, Smile, Trash2, ChevronRight,
  ChevronLeft, Rocket, Lock, GraduationCap, Briefcase,
  Users, MapPin, Calendar, BarChart, Star, Check, ChevronDown, Info, Save, Zap
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { api } from '../utils/api';
import PollCard from './PollCard';
import { useModalA11y } from '../hooks/useModalA11y';

// ─── InfoTooltip Component ───────────────────────────────────────────────────
const InfoTooltip = ({ content, align = 'left', direction = 'up' }) => (
  <span className="group relative inline-flex items-center ml-1.5 cursor-help align-middle select-none">
    <Info size={13} className="text-gray-500 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-600 transition-colors" />
    <span className={`pointer-events-none absolute w-56 p-2.5 bg-gray-900/95 dark:bg-gray-850/95 text-2xs font-black text-gray-200 dark:text-gray-300 rounded-xl shadow-xl border border-gray-800 dark:border-gray-700 opacity-0 group-hover:opacity-100 transition-all duration-200 z-50 text-center leading-normal whitespace-normal normal-case tracking-normal
      ${direction === 'down' ? 'top-full mt-4' : 'bottom-full mb-4'}
      ${align === 'right' ? 'right-0' : 'left-0'}
    `}>
      {content}
      <span className={`absolute border-4 border-transparent
        ${direction === 'down' 
          ? 'bottom-full border-b-gray-900/95 dark:border-b-gray-850/95' 
          : 'top-full border-t-gray-900/95 dark:border-t-gray-850/95'}
        ${align === 'right' ? 'right-2.5' : 'left-2.5'}
      `} />
    </span>
  </span>
);

// ─── ChipSelect ───────────────────────────────────────────────────────────────
// Accessible single-choice control that replaces the modal's old custom
// dropdowns (Type/Category/Duration/Binary Preset/Layout). Every option is
// visible at a glance instead of hidden behind an open/close menu — one tap
// to choose, no floating-menu positioning/z-index/click-outside logic to
// maintain, and no risk of the menu clipping on a narrow viewport.
//
// variant:
//   'card'   — larger tappable cards with icon + label + description, for
//              choices where the description carries real information
//              (currently just poll Type).
//   'pill'   — compact rounded pills that wrap to multiple rows (Category,
//              Binary Preset, Layout).
//   'scroll' — compact pills in a single horizontally-scrollable row, for a
//              longer list where most users just take the default (Duration).
//
// Implements the standard "radiogroup" keyboard pattern (arrow keys move
// selection, Home/End jump to the ends) since a hand-built chip picker isn't
// automatically accessible just because it isn't a dropdown.
function ChipSelect({ options, value, onChange, variant = 'pill', ariaLabel }) {
  const groupRef = useRef(null);

  const handleKeyDown = (e) => {
    const navKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!navKeys.includes(e.key)) return;
    e.preventDefault();
    const idx = options.findIndex(o => o.id === value);
    let nextIdx = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = (idx + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = (idx - 1 + options.length) % options.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = options.length - 1;
    const next = options[nextIdx];
    onChange(next);
    requestAnimationFrame(() => {
      groupRef.current?.querySelector(`[data-chip-id="${next.id}"]`)?.focus();
    });
  };

  const wrapClass = variant === 'scroll'
    ? 'flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
    : variant === 'card'
      ? 'grid grid-cols-2 sm:grid-cols-3 gap-2'
      : 'flex flex-wrap gap-1.5';

  return (
    <div ref={groupRef} role="radiogroup" aria-label={ariaLabel} onKeyDown={handleKeyDown} className={wrapClass}>
      {options.map(opt => {
        const isSelected = opt.id === value;

        if (variant === 'card') {
          // Icon sits beside the text, not above it — a stacked layout wastes
          // vertical space for what's only ever one or two short lines of text.
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              data-chip-id={opt.id}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => onChange(opt)}
              className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border-2 text-left transition-all ${isSelected
                ? 'bg-primary-600/10 dark:bg-primary-600/15 border-primary-600'
                : 'bg-gray-50 dark:bg-gray-800/80 border-transparent hover:border-gray-200 dark:hover:border-gray-700'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5 ${isSelected ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-750 text-gray-500'}`}>
                {opt.icon}
              </div>
              <div className="min-w-0">
                <p className={`text-2xs font-black uppercase tracking-wide leading-none mb-0.5 truncate ${isSelected ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`}>
                  {opt.label}
                </p>
                {opt.desc && <p className="text-2xs font-bold text-gray-500 leading-none truncate">{opt.desc}</p>}
              </div>
            </button>
          );
        }

        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            data-chip-id={opt.id}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(opt)}
            className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full border-2 text-2xs font-black uppercase tracking-wide transition-all whitespace-nowrap ${isSelected
              ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
              : 'bg-gray-50 dark:bg-gray-800/80 border-transparent text-gray-700 dark:text-gray-300 hover:border-gray-200 dark:hover:border-gray-700'}`}
          >
            {opt.icon && <span className="flex items-center">{opt.icon}</span>}
            {opt.label}
            {opt.badge}
          </button>
        );
      })}
    </div>
  );
}

function dataURLtoBlob(dataurl) {
  if (!dataurl || !dataurl.startsWith('data:')) return null;
  try {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  } catch (e) {
    console.error("Failed to convert data URL to blob:", e);
    return null;
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BINARY_PRESETS = [
  { id: 'yes_no',         label: 'Yes / No',         opts: ['Yes', 'No'],         emoji: '👍' },
  { id: 'true_false',     label: 'True / False',     opts: ['True', 'False'],     emoji: '🧠' },
  { id: 'agree_disagree', label: 'Agree / Disagree', opts: ['Agree', 'Disagree'], emoji: '🤝' }
];

const POLL_TYPES = [
  { id: 'binary',   label: 'Binary',   icon: <Activity size={16} />,    desc: 'Yes / No' },
  { id: 'single',   label: 'Single',   icon: <CheckSquare size={16} />, desc: 'One pick' },
  { id: 'multiple', label: 'Multi',    icon: <List size={16} />,        desc: 'Many picks' },
  { id: 'rank',     label: 'Rank',     icon: <BarChart2 size={16} />,   desc: 'Order them' },
  { id: 'rating',   label: 'Rating',   icon: <Star size={16} />,        desc: '1–10 scale' },
];

const CATEGORIES = [
  { id: 'tech',      label: 'Tech & Future', emoji: '⚡' },
  { id: 'finance',   label: 'Finance',       emoji: '💹' },
  { id: 'society',   label: 'Society',       emoji: '🌐' },
  { id: 'lifestyle', label: 'Lifestyle',     emoji: '✨' },
  { id: 'politics',  label: 'Politics',      emoji: '🏛️' },
  { id: 'sports',    label: 'Sports',        emoji: '🏆' },
  { id: 'science',   label: 'Science',       emoji: '🔬' },
  { id: 'health',    label: 'Health',        emoji: '🫀' },
];

const DURATIONS = [
  { label: '1h',  value: 1,   hot: true,  tip: '3× engagement velocity' },
  { label: '6h',  value: 6,   hot: false, tip: 'Good for trending topics' },
  { label: '12h', value: 12,  hot: false, tip: 'Half-day reach' },
  { label: '1d',  value: 24,  hot: false, tip: 'Most popular' },
  { label: '3d',  value: 72,  hot: false, tip: 'Deep consensus building' },
  { label: '1w',  value: 168, hot: false, tip: 'Long-form opinion' },
];

// VANE Qualifying Criteria trait definitions
const VANE_TRAITS = [
  { key: 'age',        label: 'Age Range',    icon: <Calendar size={14} />,      values: ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'] },
  { key: 'gender',     label: 'Gender',       icon: <Users size={14} />,         values: ['Male', 'Female', 'Non-Binary', 'Prefer not to say'] },
  { key: 'region',     label: 'Region',       icon: <MapPin size={14} />,        values: ['North America', 'Europe', 'Asia', 'South America', 'Africa', 'Oceania'] },
  { key: 'education',  label: 'Education',    icon: <GraduationCap size={14} />, values: ['High School', 'Some College', 'Bachelor\'s', 'Postgraduate', 'Doctorate'] },
  { key: 'workStatus', label: 'Work Status',  icon: <Briefcase size={14} />,     values: ['Student', 'Employed', 'Self-Employed', 'Unemployed', 'Retired'] },
  { key: 'sector',     label: 'Sector',       icon: <BarChart size={14} />,      values: ['Tech', 'Finance', 'Healthcare', 'Education', 'Government', 'Creative'] },
];

// Rough reach estimate percentages per trait for the estimator heuristic
const REACH_ESTIMATE = {
  education:  { 'Postgraduate': 28, 'Doctorate': 12, 'Bachelor\'s': 55, default: 40 },
  workStatus: { 'Employed': 60,  'Student': 25, 'Self-Employed': 15, default: 50 },
  gender:     { 'Male': 48, 'Female': 48, 'Non-Binary': 4, default: 50 },
  region:     { 'North America': 35, 'Europe': 28, 'Asia': 25, default: 30 },
  age:        { '25-34': 30, '18-24': 22, '35-44': 20, default: 20 },
  sector:     { 'Tech': 30, 'Finance': 15, 'Healthcare': 12, default: 18 },
};

const CATEGORY_BG = {
  tech: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800',
  finance: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&q=80&w=800',
  society: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=800',
  lifestyle: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&q=80&w=800',
  politics: 'https://images.unsplash.com/photo-1529101091760-61df6e240b92?auto=format&fit=crop&q=80&w=800',
  sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&q=80&w=800',
  science: 'https://images.unsplash.com/photo-1564325724739-bae0bd08762c?auto=format&fit=crop&q=80&w=800',
  health: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&q=80&w=800',
};

const STEP_LABELS = ['The Idea', 'The Rules', 'Audience'];

// ─── Helper: compute estimated reach ─────────────────────────────────────────
function estimateReach(criteria) {
  if (!criteria.length) return 100;
  let reach = 100;
  criteria.forEach(c => {
    // For arrays, we sum the probabilities of the OR'd values (capped at 95)
    let traitPct = 0;
    const values = Array.isArray(c.required_value) ? c.required_value : [c.required_value];
    const traitMap = REACH_ESTIMATE[c.trait_key] || {};
    
    values.forEach(v => {
      traitPct += (traitMap[v] ?? traitMap.default ?? 30);
    });
    
    traitPct = Math.min(95, traitPct);
    reach = Math.round(reach * (traitPct / 100));
  });
  return Math.max(5, reach);
}

// ─── Helper: Image Compression ──────────────────────────────────────────────
const compressImage = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const max = 1200; // Max dimension

        if (width > height) {
          if (width > max) {
            height *= max / width;
            width = max;
          }
        } else {
          if (height > max) {
            width *= max / height;
            height = max;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        canvas.toBlob((blob) => {
          resolve({ dataUrl, blob: blob || file });
        }, 'image/jpeg', 0.75);
      };
      img.onerror = () => resolve({ dataUrl: event.target.result, blob: file });
    };
    reader.onerror = () => resolve(null);
  });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepHeader({ step, onClose }) {
  return (
    <div className="flex items-center justify-between px-8 pt-7 pb-5 flex-shrink-0 border-b border-gray-100 dark:border-gray-800/80">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/20">
          <Plus size={22} className="text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            {STEP_LABELS.map((label, i) => (
              <React.Fragment key={i}>
                <span className={`text-2xs font-black uppercase tracking-widest transition-colors ${i + 1 === step ? 'text-primary-600' : i + 1 < step ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`}>
                  {i + 1 < step ? <Check size={10} className="inline mr-0.5 mb-0.5" /> : null}
                  {label}
                </span>
                {i < STEP_LABELS.length - 1 && <span className="text-gray-200 dark:text-gray-700 text-2xs">›</span>}
              </React.Fragment>
            ))}
          </div>
          <h2 className="text-xl font-black uppercase italic tracking-tighter text-gray-900 dark:text-white leading-none">
            {STEP_LABELS[step - 1]}
          </h2>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Progress bar */}
        <div className="hidden sm:flex items-center gap-2">
          {[1, 2, 3].map(i => (
            <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i <= step ? 'bg-primary-600 w-8' : 'bg-gray-200 dark:bg-gray-700 w-4'}`} />
          ))}
        </div>
        <button
          onClick={onClose}
          aria-label="Close poll creator"
          className="p-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

function LivePreview({ question, selectedType, selectedCategory, options, selectedDuration, pollImage, optionImages = {}, selectedLayout = 'auto' }) {
  const cat = CATEGORIES.find(c => c.id === selectedCategory) || CATEGORIES[0];
  const bg = pollImage || CATEGORY_BG[selectedCategory] || CATEGORY_BG.tech;
  const dur = DURATIONS.find(d => d.value === selectedDuration);
  
  const previewOptions = selectedType === 'binary' 
    ? (options.filter(o => o.trim()).length > 0 ? options.filter(o => o.trim()) : ['Yes', 'No'])
    : selectedType === 'rating' 
      ? ['1','2','3','4','5','6','7','8','9','10'] 
      : (options.filter(o => o.trim()).length > 0 ? options.filter(o => o.trim()) : ['Option A', 'Option B']);

  const mappedOptions = previewOptions.map((label, idx) => {
    const imgObj = optionImages[idx];
    return {
      label,
      img: (imgObj && typeof imgObj === 'object' ? imgObj.preview : imgObj) || null
    };
  });

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden">
      <div className="flex-1 flex items-center justify-center pointer-events-none transform scale-[0.85] origin-top">
        <div className="w-full min-w-[400px]">
          <PollCard
            id="preview"
            question={question || 'Your question will appear here...'}
            type={selectedType}
            category={cat.label}
            options={mappedOptions}
            totalVoters={0}
            timeLeft={(dur?.value || 24) * 3600}
            img={(pollImage && typeof pollImage === 'object' ? pollImage.preview : pollImage) || bg}
            compact={false}
            inModal={false}
            votedStatus="idle"
            layout={selectedLayout}
          />
        </div>
      </div>

      {/* Social proof */}
      <div className="mt-4 p-4 flex-shrink-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/10 rounded-2xl border border-purple-100/50 dark:border-purple-800/20">
        <p className="text-[11px] text-gray-600 dark:text-gray-400 font-bold leading-snug">
          <span className="text-primary-600 font-black">🔥 Trending stats:</span> Polls in{' '}
          <strong>{cat.label}</strong> get an avg of <strong>340+ votes</strong> in their first 24h.
        </p>
      </div>
    </div>
  );
}

// ─── Qualifying Criteria Builder ──────────────────────────────────────────────

function CriteriaBuilder({ criteria, setCriteria }) {
  // We keep track of which trait is currently being edited.
  const [selectedTrait, setSelectedTrait] = useState('age');
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  
  const reach = estimateReach(criteria);
  const activeCriterionIndex = criteria.findIndex(c => c.trait_key === selectedTrait);
  const activeCriterion = activeCriterionIndex !== -1 ? criteria[activeCriterionIndex] : null;

  const toggleValue = (value) => {
    let newCriteria = [...criteria];
    
    if (activeCriterion) {
      // Toggle value within existing array
      let currentValues = Array.isArray(activeCriterion.required_value) 
        ? [...activeCriterion.required_value] 
        : [activeCriterion.required_value];
        
      if (currentValues.includes(value)) {
        currentValues = currentValues.filter(v => v !== value);
      } else {
        currentValues.push(value);
      }
      
      if (currentValues.length === 0) {
        // Remove criterion entirely if empty
        newCriteria = newCriteria.filter(c => c.trait_key !== selectedTrait);
      } else {
        newCriteria[activeCriterionIndex] = { ...activeCriterion, required_value: currentValues };
      }
    } else {
      // Add new criterion
      if (criteria.length >= 6) return; // Max 6 trait gates
      const traitDef = VANE_TRAITS.find(t => t.key === selectedTrait);
      newCriteria.push({
        trait_key: selectedTrait,
        required_value: [value],
        prompt: `What is your ${traitDef?.label?.toLowerCase()}?`,
        options: traitDef?.values || [],
      });
    }
    
    setCriteria(newCriteria);
  };

  const removeGate = (key) => {
    setCriteria(criteria.filter(c => c.trait_key !== key));
  };

  const traitIcon = (key) => VANE_TRAITS.find(t => t.key === key)?.icon;

  return (
    <div className="space-y-4">
      {/* Trait Tabs */}
      <div className="flex flex-wrap gap-2 pb-2">
        {VANE_TRAITS.map(t => {
          const isActive = criteria.find(c => c.trait_key === t.key);
          const isSelected = selectedTrait === t.key;
          const disabled = false;
          
          return (
            <button
              key={t.key}
              onClick={() => setSelectedTrait(t.key)}
              disabled={disabled}
              className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border
                ${isSelected 
                  ? 'bg-white dark:bg-gray-700 border-primary-600 shadow-sm text-gray-900 dark:text-white' 
                  : isActive
                    ? 'bg-primary-600/10 dark:bg-primary-600/20 border-primary-600/30 text-primary-600'
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-white dark:hover:bg-gray-700'
                }`}
            >
              {t.icon}
              {t.label}
              {isActive && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-2xs font-black ${
                  isSelected 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-primary-600/20 text-primary-600'
                }`}>
                  {Array.isArray(isActive.required_value) ? isActive.required_value.length : 1}
                </span>
              )}
              {isActive && !isSelected && (
                <div onClick={(e) => { e.stopPropagation(); removeGate(t.key); }} className="ml-1 hover:text-red-500">
                  <X size={12} />
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Value selector chips */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-2xs font-bold text-gray-500 uppercase tracking-widest">
            Select accepted {VANE_TRAITS.find(t => t.key === selectedTrait)?.label.toLowerCase()}s (OR)
          </p>
          <button
            onClick={() => {
              const traitDef = VANE_TRAITS.find(t => t.key === selectedTrait);
              const allValues = traitDef?.values || [];
              const currentValues = activeCriterion ? (Array.isArray(activeCriterion.required_value) ? activeCriterion.required_value : [activeCriterion.required_value]) : [];
              
              let newCriteria = [...criteria];
              if (currentValues.length === allValues.length) {
                // Deselect all -> remove criterion
                newCriteria = newCriteria.filter(c => c.trait_key !== selectedTrait);
              } else {
                // Select all
                if (activeCriterion) {
                  newCriteria[activeCriterionIndex] = { ...activeCriterion, required_value: allValues };
                } else {
                  if (criteria.length >= 6) return;
                  newCriteria.push({
                    trait_key: selectedTrait,
                    required_value: allValues,
                    prompt: `What is your ${traitDef?.label?.toLowerCase()}?`,
                    options: allValues,
                  });
                }
              }
              setCriteria(newCriteria);
            }}
            className="text-2xs font-black text-primary-600 uppercase tracking-wider hover:underline"
          >
            {activeCriterion && (Array.isArray(activeCriterion.required_value) ? activeCriterion.required_value.length : 1) === (VANE_TRAITS.find(t => t.key === selectedTrait)?.values.length) ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(VANE_TRAITS.find(t => t.key === selectedTrait)?.values || []).map(val => {
            const currentValues = activeCriterion ? (Array.isArray(activeCriterion.required_value) ? activeCriterion.required_value : [activeCriterion.required_value]) : [];
            const isSelected = currentValues.includes(val);
            
            return (
              <button
                key={val}
                onClick={() => toggleValue(val)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all flex items-center gap-1.5
                  ${isSelected
                    ? 'bg-primary-600 border-primary-600 text-white shadow-md'
                    : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-600/50'
                  }`}
              >
                {isSelected && <Check size={12} />}
                {val}
              </button>
            );
          })}
        </div>
        {criteria.length >= 6 && !activeCriterion && (
          <p className="text-2xs text-amber-500 font-bold italic mt-3">Precision limit reached (Max 6 traits).</p>
        )}
      </div>

      {/* Active constraints summary */}
      {criteria.length > 0 && (
        <div className="bg-primary-600/5 dark:bg-primary-600/10 rounded-2xl border border-primary-600/20 overflow-hidden transition-all duration-200">
          <button
            type="button"
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            className="w-full flex items-center justify-between p-4 text-left font-black text-primary-600 uppercase text-2xs tracking-widest hover:bg-primary-600/5 transition-all"
          >
            <span className="flex items-center gap-1.5">
              <span>Gate Summary</span>
              <span className="px-2 py-0.5 rounded-full bg-primary-600/10 text-2xs lowercase tracking-normal font-black">
                {criteria.length} {criteria.length === 1 ? 'filter' : 'filters'} active
              </span>
            </span>
            <ChevronDown size={14} className={`transition-transform duration-200 ${isSummaryExpanded ? 'rotate-180' : ''}`} />
          </button>
          
          {isSummaryExpanded && (
            <div className="p-4 pt-0 border-t border-primary-600/10 space-y-3 animate-in slide-in-from-top-2 duration-200">
              <div className="space-y-2.5 pt-3">
                {criteria.map((c) => {
                  const traitDef = VANE_TRAITS.find(t => t.key === c.trait_key);
                  const vals = Array.isArray(c.required_value) ? c.required_value : [c.required_value];
                  return (
                    <div key={c.trait_key} className="flex flex-col sm:flex-row sm:items-center gap-2 py-1.5 border-b border-primary-600/10 last:border-0">
                      <div className="flex items-center gap-1.5 min-w-[130px] text-xs font-bold text-gray-700 dark:text-gray-300">
                        <span className="text-primary-600 flex-shrink-0">{traitDef?.icon}</span>
                        <span>{traitDef?.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {vals.map((v, idx) => (
                          <React.Fragment key={v}>
                            {idx > 0 && <span className="text-2xs text-gray-500 dark:text-gray-600 font-bold">OR</span>}
                            <span className="px-2.5 py-0.5 rounded-md bg-primary-600/10 dark:bg-primary-600/20 text-primary-600 text-[11px] font-black uppercase tracking-tight">
                              {v}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-2xs text-gray-500 dark:text-gray-500 font-bold mt-2">
                * Voters must satisfy ALL active demographic categories listed above to participate.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export function CreatePollModal({ onCreate, onClose, isVerified, onVerify }) {
  const containerRef = useModalA11y(true, onClose);

  // Core form state
  const [step, setStep] = useState(1);
  const [inputText, setInputText] = useState('');
  const [selectedType, setSelectedType] = useState('binary');
  const [selectedCategory, setSelectedCategory] = useState('tech');
  const [options, setOptions] = useState(() => {
    try {
      const saved = localStorage.getItem('create_poll_draft_v2');
      if (saved) {
        const d = JSON.parse(saved);
        if (d.options?.length >= 2) return d.options;
      }
    } catch (_) {}
    return ['Yes', 'No'];
  });
  const [binaryPreset, setBinaryPreset] = useState(() => {
    try {
      const saved = localStorage.getItem('create_poll_draft_v2');
      if (saved) {
        const d = JSON.parse(saved);
        if (d.options?.length === 2) {
          const optStr = d.options.join(',');
          if (optStr === 'Yes,No') return 'yes_no';
          if (optStr === 'True,False') return 'true_false';
          if (optStr === 'Agree,Disagree') return 'agree_disagree';
          return 'yes_no';
        }
      }
    } catch (_) {}
    return 'yes_no';
  });
  const [selectedDuration, setSelectedDuration] = useState(24);
  const [isPrivate, setIsPrivate] = useState(false);
  const [pollImage, setPollImage] = useState(null);
  const [optionImages, setOptionImages] = useState({}); // { 0: 'data...', 1: 'data...' }
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [creatorCriteria, setCreatorCriteria] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('auto');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateDetected, setDuplicateDetected] = useState(false);
  const [typing, setTyping] = useState(false);
  const [showDraftSuccess, setShowDraftSuccess] = useState(false);
  const typingTimer = useRef(null);

  // All poll-creation state is auto-saved to this single localStorage key as the user types
  // (see the debounced-by-state-change useEffect below), and restored on next modal open —
  // so closing the modal or reloading the page does not lose in-progress poll drafts. Image
  // blobs themselves are NOT persisted (they can't be JSON-serialized) — only the base64
  // `preview` data URLs are kept, so a restored draft's images are preview-only until the
  // user re-attaches/re-uploads them at submit time.
  const DRAFT_KEY = 'create_poll_draft_v2';

  const handleSaveDraft = () => {
    try {
      const savedOptionImages = Object.keys(optionImages).reduce((acc, key) => {
        if (optionImages[key] && optionImages[key].preview) {
          acc[key] = { preview: optionImages[key].preview };
        }
        return acc;
      }, {});
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        inputText,
        selectedType,
        options,
        selectedCategory,
        selectedDuration,
        selectedLayout,
        pollImage: pollImage && pollImage.preview ? { preview: pollImage.preview } : null,
        optionImages: savedOptionImages
      }));
      setShowDraftSuccess(true);
      setTimeout(() => setShowDraftSuccess(false), 2000);
    } catch (_) {}
  };

  // Restore draft on open
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const d = JSON.parse(saved);
        if (d.inputText) setInputText(d.inputText);
        if (d.selectedType) setSelectedType(d.selectedType);
        if (d.options?.length >= 2) setOptions(d.options);
        if (d.selectedCategory) setSelectedCategory(d.selectedCategory);
        if (d.selectedDuration) setSelectedDuration(d.selectedDuration);
        if (d.selectedLayout) setSelectedLayout(d.selectedLayout);
        if (d.pollImage) setPollImage(d.pollImage);
        if (d.optionImages) setOptionImages(d.optionImages);
      }
    } catch (_) {}
  }, []);

  // Auto-save draft
  useEffect(() => {
    if (!inputText && options.every(o => !o) && !pollImage && Object.keys(optionImages).length === 0) return;
    try {
      const savedOptionImages = Object.keys(optionImages).reduce((acc, key) => {
        if (optionImages[key] && optionImages[key].preview) {
          acc[key] = { preview: optionImages[key].preview };
        }
        return acc;
      }, {});
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        inputText,
        selectedType,
        options,
        selectedCategory,
        selectedDuration,
        selectedLayout,
        pollImage: pollImage && pollImage.preview ? { preview: pollImage.preview } : null,
        optionImages: savedOptionImages
      }));
    } catch (_) {}
  }, [inputText, selectedType, options, selectedCategory, selectedDuration, selectedLayout, pollImage, optionImages]);

  // Duplicate detection (simple heuristic): this is NOT a real similarity check against
  // existing polls — it only flags the literal substring "agi" (e.g. from "AGI") anywhere in
  // the question text. It appears to be a hardcoded demo/placeholder for a smarter duplicate
  // detector rather than a general-purpose implementation.
  useEffect(() => {
    setDuplicateDetected(inputText.toLowerCase().includes('agi'));
  }, [inputText]);

  // Auto-select layout style based on number of option images uploaded
  useEffect(() => {
    const imgCount = Object.keys(optionImages).length;
    if (imgCount <= 1) {
      if (selectedLayout !== 'auto') {
        setSelectedLayout('auto');
      }
    } else if (imgCount === 2 || imgCount === 3) {
      // If we only have 2 or 3 images, 'grid' is not available. Switch to columns if it was grid/auto/hero
      if (selectedLayout === 'auto' || selectedLayout === 'hero' || selectedLayout === 'grid') {
        setSelectedLayout('columns');
      }
    } else if (imgCount >= 4) {
      // Default to grid layout if 4 or more images are uploaded
      if (selectedLayout === 'auto' || selectedLayout === 'hero' || selectedLayout === 'columns') {
        setSelectedLayout('grid');
      }
    }
  }, [optionImages, selectedLayout]);

  // Typing momentum indicator
  const handleQuestionChange = (e) => {
    setInputText(e.target.value);
    setTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setTyping(false), 800);
  };

  // Option handlers
  const handleOptionChange = (idx, val) => {
    const n = [...options]; n[idx] = val; setOptions(n);
  };
  const addOption = () => { if (options.length < 10) setOptions([...options, '']); };
  const removeOption = (idx) => { 
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== idx));
      setOptionImages(prev => {
        const next = {};
        Object.keys(prev).forEach(key => {
          const k = parseInt(key);
          if (k < idx) next[k] = prev[k];
          if (k > idx) next[k - 1] = prev[k];
        });
        return next;
      });
    }
  };
  const handleEmojiClick = (emojiData, idx) => {
    const n = [...options]; n[idx] += emojiData.emoji; setOptions(n);
    setShowEmojiPicker(null);
  };

  const handleImageUpload = async (e, index = null) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        const imgObj = { preview: compressed.dataUrl, blob: compressed.blob };
        
        if (index !== null) {
          setOptionImages(prev => ({ ...prev, [index]: imgObj }));
        } else {
          setPollImage(imgObj);
        }
      } catch (err) {
        console.error("Compression failed, using original:", err);
        const preview = URL.createObjectURL(file);
        const imgObj = { preview, blob: file };
        if (index !== null) {
          setOptionImages(prev => ({ ...prev, [index]: imgObj }));
        } else {
          setPollImage(imgObj);
        }
      }
    }
  };

  const canProceedStep1 = inputText.trim().length > 0;
  const canProceedStep2 = selectedType === 'binary' || selectedType === 'rating' || options.filter(o => o.trim()).length >= 2;

  const handleSubmit = async () => {
    if (!inputText.trim() || isSubmitting) return;

    if (!isVerified) {
      if (onVerify) onVerify();
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      // --- PERFECT IMAGE MANAGEMENT: Upload Blobs First ---
      let uploadedPollImage = null;
      let finalPollImageBlob = pollImage?.blob;
      if (!finalPollImageBlob && pollImage?.preview && pollImage.preview.startsWith('data:')) {
        finalPollImageBlob = dataURLtoBlob(pollImage.preview);
      }
      if (finalPollImageBlob) {
        const media = await api.uploadMedia(finalPollImageBlob, null, 'poll_image');
        uploadedPollImage = media.url;
      }

      const uploadedOptionImages = {};
      for (const idx of Object.keys(optionImages)) {
        const imgObj = optionImages[idx];
        if (imgObj) {
          let finalBlob = imgObj.blob;
          if (!finalBlob && imgObj.preview && imgObj.preview.startsWith('data:')) {
            finalBlob = dataURLtoBlob(imgObj.preview);
          }
          if (finalBlob) {
            const media = await api.uploadMedia(finalBlob, null, 'option_image');
            uploadedOptionImages[idx] = media.url;
          } else if (typeof imgObj === 'string') {
            uploadedOptionImages[idx] = imgObj; // Already a URL (e.g. from generator)
          } else if (imgObj.preview) {
            uploadedOptionImages[idx] = imgObj.preview;
          }
        }
      }

      await onCreate({
        question: inputText,
        type: selectedType,
        poll_type: selectedType,
        category: CATEGORIES.find(c => c.id === selectedCategory)?.label || 'Tech & Future',
        options: (
          selectedType === 'binary' ? ['Yes', 'No'] : 
          selectedType === 'rating' ? ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] : 
          options.filter(o => o.trim())
        ).map((label, idx) => ({
          label,
          img: uploadedOptionImages[idx] || null
        })),
        isPrivate,
        isGlobal: true,
        duration_hours: selectedDuration,
        img: uploadedPollImage || CATEGORY_BG[selectedCategory] || CATEGORY_BG.tech,
        creator_criteria: creatorCriteria,
        multi_img: Object.keys(uploadedOptionImages).length > 0,
        layout: selectedLayout
      });
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
    } catch (err) {
      if (err.message?.includes('consensus')) {
        setError('⚡ A similar poll is already gaining consensus. Consider joining it instead.');
        setStep(1);
      } else if (err.message?.includes('metabolism') || err.message?.includes('429')) {
        setError('🕐 Your Ghost needs more reputation before posting again. Vote on a few polls first.');
      } else {
        setError(err.message || 'Failed to launch poll. Try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Step 1: The Idea ────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-8 pb-36 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Question input */}
      <div className="space-y-3">
        <div className="flex justify-between items-center px-1">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] flex items-center">
            Question
            <InfoTooltip direction="down" content="Write a compelling, concise question that invites debate. Keep it neutral to avoid bias." />
          </label>
          <div className={`text-2xs font-black tracking-[0.2em] transition-colors ${step >= 1 ? 'text-primary-600' : 'text-gray-300 dark:text-gray-600'}`}>
            {step > 1 ? <div className="flex items-center gap-1"><Check size={10} strokeWidth={4} /> THE IDEA</div> : 'THE IDEA'}
          </div>
        </div>
        <textarea
          value={inputText}
          onChange={handleQuestionChange}
          maxLength={140}
          placeholder="What does the world need to decide?"
          autoFocus
          rows={3}
          className={`w-full bg-gray-50 dark:bg-gray-800/80 rounded-2xl p-5 text-xl font-black outline-none transition-all placeholder:text-gray-300 dark:placeholder:text-gray-600 resize-none text-gray-900 dark:text-white border-2 ${typing ? 'border-primary-600 shadow-lg shadow-purple-500/10' : 'border-transparent focus:border-primary-600/40'}`}
        />
        {duplicateDetected && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700/30">
            <ShieldAlert size={16} className="text-amber-500 flex-shrink-0" />
            <p className="text-[11px] text-amber-700 dark:text-amber-400 font-bold">Similar topic detected — you may get more reach by boosting an existing poll.</p>
          </div>
        )}
      </div>

      {/* Selectors — chip pickers instead of the old dropdown menus (see ChipSelect) */}
      <div className="space-y-4">
        {/* Poll type */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] px-1 flex items-center">
            Type
            <InfoTooltip content="Choose the consensus mechanism: Binary (Yes/No), Single selection, Multi-selection, Rank ordering, or 1–10 Rating scale." />
          </label>
          <ChipSelect
            variant="card"
            ariaLabel="Poll type"
            options={POLL_TYPES}
            value={selectedType}
            onChange={(t) => {
              setSelectedType(t.id);
              if (t.id === 'binary') {
                setOptions(['Yes', 'No']);
                setBinaryPreset('yes_no');
              } else if (t.id === 'rating') {
                setOptions(['1', '10']);
              } else {
                setOptions(['', '']);
              }
            }}
          />
        </div>

        {/* Category */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] px-1 flex items-center">
            Category
            <InfoTooltip content="Tag your poll with a relevant category to target the correct user feeds and index it properly." />
          </label>
          <ChipSelect
            variant="pill"
            ariaLabel="Poll category"
            options={CATEGORIES.map(c => ({ id: c.id, label: c.label, icon: <span className="text-sm leading-none">{c.emoji}</span> }))}
            value={selectedCategory}
            onChange={(c) => setSelectedCategory(c.id)}
          />
        </div>

        {/* Duration */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] px-1 flex items-center">
            Duration
            <InfoTooltip content="Determine how long the consensus engine will run before final mathematical results are locked." />
          </label>
          <ChipSelect
            variant="scroll"
            ariaLabel="Poll duration"
            options={DURATIONS.map(d => ({
              id: String(d.value),
              label: d.label,
              badge: d.hot ? <span className="ml-0.5">🔥</span> : null,
            }))}
            value={String(selectedDuration)}
            onChange={(d) => setSelectedDuration(Number(d.id))}
          />
          <p className="text-2xs font-bold text-gray-500 px-1">
            {DURATIONS.find(d => d.value === selectedDuration)?.tip}
          </p>
        </div>
      </div>
    </div>
  );

  // ─── Step 2: The Rules ───────────────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Options */}
      <div className="space-y-4">
        <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] px-1 flex justify-between items-center">
          <span className="flex items-center">
            {selectedType === 'binary' ? 'Binary Choice' : selectedType === 'rating' ? 'Rating Scale' : 'Answer Options'}
            <InfoTooltip direction="down" content="Provide up to 10 choices for voters. Add custom images or emojis to make options stand out." />
          </span>
          {(selectedType !== 'binary' && selectedType !== 'rating') && (
            <span className="normal-case tracking-normal text-2xs font-bold text-primary-600/60">{options.length}/10</span>
          )}
        </label>

        {/* Binary Presets */}
        {selectedType === 'binary' && (
          <div className="space-y-1.5 animate-in fade-in zoom-in-95 duration-200">
            <label className="text-2xs font-black text-gray-500 uppercase tracking-widest px-1 flex items-center">
              Binary Option Preset
            </label>
            <ChipSelect
              variant="pill"
              ariaLabel="Binary option preset"
              options={BINARY_PRESETS.map(p => ({ id: p.id, label: p.label, icon: <span>{p.emoji}</span> }))}
              value={binaryPreset}
              onChange={(p) => {
                setBinaryPreset(p.id);
                setOptions(BINARY_PRESETS.find(bp => bp.id === p.id).opts);
              }}
            />
          </div>
        )}

        {/* Rating scale info summary */}
        {selectedType === 'rating' && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-[20px] border border-gray-100 dark:border-gray-800/50">
            <p className="text-xs font-black uppercase text-gray-700 dark:text-gray-300 mb-1">1 to 10 Rating Scale</p>
            <p className="text-2xs font-bold text-gray-500">Consensus will be calculated dynamically based on average weighted ratings from users.</p>
          </div>
        )}

        {/* Single, Multi, Rank Options */}
        {(selectedType !== 'binary' && selectedType !== 'rating') && (
          <div className="space-y-3.5">
            {options.map((opt, idx) => (
              <div key={idx} className="relative">
                <div className="flex items-center gap-3">
                  <span className="text-2xs font-black text-gray-300 dark:text-gray-600 w-5 text-right flex-shrink-0">{idx + 1}.</span>
                  
                  {/* Option Text Input container */}
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={opt}
                      onChange={e => handleOptionChange(idx, e.target.value)}
                      placeholder={`Option ${idx + 1}`}
                      className="w-full bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl px-5 py-3 pr-12 text-sm font-black focus:border-primary-600 outline-none transition-all text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-gray-600"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(showEmojiPicker === idx ? null : idx)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 text-gray-300 dark:text-gray-600 hover:text-primary-600 transition-all"
                    >
                      <Smile size={18} />
                    </button>
                  </div>

                  {/* Option Image Upload Button on the right of input */}
                  {optionImages[idx] ? (
                    <div className="relative flex-shrink-0 group/img animate-in zoom-in duration-200">
                      <div className="w-11 h-11 rounded-2xl overflow-hidden border-2 border-primary-600 shadow-sm shadow-purple-500/10">
                        <img src={optionImages[idx]?.preview || optionImages[idx]} className="w-full h-full object-cover" />
                      </div>
                      <button 
                        type="button"
                        onClick={() => setOptionImages(prev => {
                          const next = { ...prev };
                          delete next[idx];
                          return next;
                        })}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md transition-all active:scale-90 z-10"
                      >
                        <X size={10} strokeWidth={3} />
                      </button>
                    </div>
                  ) : (
                    <label 
                      title="Upload option image"
                      className="w-11 h-11 bg-gray-50 dark:bg-gray-800 hover:bg-primary-600/10 hover:text-primary-600 border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-primary-600/40 rounded-2xl flex items-center justify-center cursor-pointer transition-all flex-shrink-0 group relative"
                    >
                      <div className="relative">
                        <ImageIcon size={16} className="text-gray-500 group-hover:text-primary-600 transition-colors" />
                        <div className="absolute -bottom-1 -right-1 bg-primary-600 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-2xs font-black shadow-sm group-hover:scale-110 transition-transform">
                          +
                        </div>
                      </div>
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, idx)} />
                    </label>
                  )}

                  {/* Remove option button */}
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(idx)}
                      className="p-2 text-gray-300 dark:text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                {showEmojiPicker === idx && (
                  <div className="absolute left-6 top-full mt-1.5 z-50">
                    <div className="fixed inset-0" onClick={() => setShowEmojiPicker(null)} />
                    <div className="relative">
                      <EmojiPicker
                        onEmojiClick={d => handleEmojiClick(d, idx)}
                        theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                        width={300} height={380}
                        skinTonesDisabled
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {options.length < 10 && (
              <button
                type="button"
                onClick={addOption}
                className="w-full py-2.5 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-gray-500 text-xs font-black uppercase tracking-widest hover:border-primary-600 hover:text-primary-600 transition-all"
              >
                + Add Option
              </button>
            )}
          </div>
        )}
      </div>

      {/* Collage Layout Selector (Only if multiple images) */}
      {Object.keys(optionImages).length > 1 && (
        <div className="space-y-1.5">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] px-1 flex items-center">
            Collage Style
            <InfoTooltip content="Select how multiple uploaded option images will be dynamically stitched together in the feed collage." />
          </label>

          {(() => {
            const count = Object.keys(optionImages).length;
            const layouts = [
              { id: 'columns', label: 'Columns', desc: 'Vertical splits', icon: '📊' },
              { id: 'rows',    label: 'Rows',    desc: 'Horizontal rows', icon: '🥞' },
            ];
            if (count >= 4) {
              layouts.push({ id: 'grid', label: 'Grid', desc: 'Quad collage grid', icon: '🎛️' });
            }
            return (
              <ChipSelect
                variant="pill"
                ariaLabel="Collage layout style"
                options={layouts.map(l => ({ id: l.id, label: l.label, icon: <span>{l.icon}</span> }))}
                value={selectedLayout}
                onChange={(l) => setSelectedLayout(l.id)}
              />
            );
          })()}
        </div>
      )}

      {/* Main Header Image Upload (Only shown if no option images uploaded) */}
      {Object.keys(optionImages).length === 0 && (
        <div className="space-y-3 pt-2">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] px-1 flex items-center">
            Main Header Image (Optional)
            <InfoTooltip content="Upload a striking cover image to serve as the background billboard for your poll card." />
          </label>
          {!pollImage ? (
            <label className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-[20px] cursor-pointer hover:border-primary-600/40 hover:bg-primary-600/5 transition-all group">
              <div className="w-10 h-10 bg-gray-50 dark:bg-gray-900 rounded-xl flex items-center justify-center group-hover:bg-primary-600/10">
                <ImageIcon size={20} className="text-primary-600/40 animate-pulse" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-black text-gray-700 dark:text-gray-300">Set a custom background</p>
                <p className="text-2xs font-bold text-gray-500">Replaces category default</p>
              </div>
              <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e)} />
              <Plus size={16} className="text-gray-300" />
            </label>
          ) : (
            <div className="relative rounded-[24px] overflow-hidden h-32 border-2 border-primary-600/20 group animate-in zoom-in duration-200">
              <img src={pollImage?.preview || pollImage} className="w-full h-full object-cover" />
              <button onClick={() => setPollImage(null)} className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full hover:bg-red-500 transition-colors">
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ─── Step 3: Audience & Launch ───────────────────────────────────────────────
  const renderStep3 = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Target Audience */}
      <div className="space-y-4">
        <div className="flex justify-between items-center px-1">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] flex items-center">
            Who should see this?
            <InfoTooltip direction="down" content="Target specific user demographics using cryptographic traits. Open globally by default." />
          </label>
          <div className="flex items-center gap-2">
            {creatorCriteria.length === 0 ? (
              <div className="flex items-center gap-1.5 bg-green-500/10 text-green-600 px-3 py-1 rounded-full text-2xs font-black uppercase tracking-wider">
                <Globe size={10} /> Open to Everyone
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreatorCriteria([])}
                className="text-2xs font-black text-red-500 hover:text-red-600 uppercase tracking-wider px-2.5 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 border border-red-500/20 hover:border-red-500/30 transition-all flex items-center gap-1"
              >
                <Trash2 size={10} /> Reset
              </button>
            )}
          </div>
        </div>
        <div className="p-5 bg-gray-50 dark:bg-gray-800/60 rounded-3xl border border-gray-200/50 dark:border-gray-700/50">
          <CriteriaBuilder criteria={creatorCriteria} setCriteria={setCreatorCriteria} />
        </div>
      </div>

      {/* Discovery Mode (Visibility) */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-1">
          <label className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] flex items-center">
            Discovery Mode
            <InfoTooltip direction="down" content="Choose if this poll is indexed publicly on the Global Feed or accessible only via private link." />
          </label>
          <div className="flex items-center gap-1.5 text-2xs font-bold text-gray-500 dark:text-gray-500 text-right leading-none">
            <AlertCircle size={12} className="text-primary-600/60 flex-shrink-0" />
            <span>
              {isPrivate 
                ? "Private: invite-only & hidden from the Global Feed."
                : "Public: served to the global network."
              }
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: false, label: 'Public Feed', desc: 'Global discovery & trends', icon: <Globe size={18} /> },
            { id: true,  label: 'Private Link', desc: 'Direct invite only', icon: <Lock size={18} /> }
          ].map(mode => (
            <button
              key={String(mode.id)}
              onClick={() => setIsPrivate(mode.id)}
              className={`p-4 rounded-2xl border-2 text-left transition-all relative ${
                isPrivate === mode.id 
                  ? 'bg-primary-600/5 border-primary-600 shadow-sm' 
                  : 'bg-gray-50 dark:bg-gray-800/80 border-transparent hover:border-gray-200 dark:hover:border-gray-700'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${
                isPrivate === mode.id 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-primary-600/10 text-primary-600'
              }`}>
                {mode.icon}
              </div>
              <p className={`text-sm font-black uppercase tracking-tight ${isPrivate === mode.id ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`}>{mode.label}</p>
              <p className="text-2xs font-bold text-gray-500 mt-0.5">{mode.desc}</p>
              {isPrivate === mode.id && <div className="absolute top-4 right-4 text-primary-600"><Check size={16} strokeWidth={3} /></div>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 md:p-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" onClick={onClose} />

      <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Create a new poll" className="relative w-full max-w-[1100px] bg-white dark:bg-gray-900 rounded-panel shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden"
        style={{ height: 'min(92vh, 720px)' }}
      >
        {/* Two-panel layout */}
        <div className="flex h-full">

          {/* ── Left: Wizard ── */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <StepHeader step={step} onClose={onClose} />

            {/* Draft success banner */}
            {showDraftSuccess && (
              <div className="mx-6 mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 rounded-2xl flex items-center gap-2.5 animate-in slide-in-from-top-2 duration-300">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Check size={12} strokeWidth={3} />
                </div>
                <p className="text-[11px] text-primary-600 font-black uppercase tracking-wider">Draft successfully saved locally!</p>
              </div>
            )}

            {/* Error banner */}
            {error && (
              <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-xl flex items-start gap-2.5">
                <AlertCircle size={15} className="text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-red-700 dark:text-red-400 font-bold">{error}</p>
                <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X size={13} /></button>
              </div>
            )}

            {/* Step content */}
            <div className="flex-1 overflow-y-auto px-8 py-6 no-scrollbar">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </div>

            {/* Navigation footer */}
            <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-800 px-8 py-5 flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-1.5 px-5 py-3.5 rounded-2xl border border-gray-200 dark:border-gray-700 text-xs font-black uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                >
                  <ChevronLeft size={14} /> Back
                </button>
              )}

              <button
                type="button"
                onClick={handleSaveDraft}
                title="Save Draft"
                aria-label="Save draft"
                className="flex items-center gap-1.5 px-4 py-3.5 rounded-2xl border-2 border-dashed border-primary-600/20 dark:border-gray-700 hover:border-primary-600/40 text-xs font-black uppercase tracking-wider text-primary-600 hover:bg-primary-600/5 transition-all"
              >
                <Save size={14} />
              </button>

              {/* Quick Launch: the defaults (binary Yes/No, 24h, no audience gating) are
                  already a complete poll — most people don't need steps 2-3 at all, so this
                  skips straight to submit instead of forcing three screens for a one-line
                  yes/no question. "Continue" is still there for anyone who wants to customize. */}
              {step === 1 && canProceedStep1 && selectedType === 'binary' && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  title="Launch immediately with Yes/No options, 24h duration, and no audience restrictions"
                  className="flex items-center gap-1.5 px-4 py-3.5 rounded-2xl border-2 border-primary-600/30 text-primary-600 hover:bg-primary-600 hover:text-white text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap"
                >
                  <Zap size={14} /> Quick Launch
                </button>
              )}

              {step < 3 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-sm uppercase tracking-wider transition-all ${((step === 1 && canProceedStep1) || (step === 2 && canProceedStep2)) ? 'bg-primary-600 text-white shadow-xl shadow-purple-500/25 hover:bg-primary-700 hover:shadow-purple-500/40 active:scale-[0.98]' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed'}`}
                >
                  Continue <ChevronRight size={15} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!inputText.trim() || isSubmitting}
                  className={`flex-1 flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-black text-sm uppercase tracking-wider transition-all ${inputText.trim() && !isSubmitting ? 'bg-primary-600 text-white shadow-xl shadow-purple-500/25 hover:bg-primary-700 active:scale-[0.98]' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed'}`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Launching...
                    </>
                  ) : (
                    <>
                      {isVerified ? <Rocket size={16} /> : <Lock size={16} />}
                      <span>{isVerified ? 'Launch Poll' : 'Login to Launch'}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ── Right: Live Preview (hidden on mobile) ── */}
          <div className="hidden lg:flex w-[380px] border-l border-gray-100 dark:border-gray-800 flex-col bg-gray-50/50 dark:bg-gray-900/50">
            <LivePreview
              question={inputText}
              selectedType={selectedType}
              selectedCategory={selectedCategory}
              options={options}
              selectedDuration={selectedDuration}
              pollImage={pollImage}
              optionImages={optionImages}
              selectedLayout={selectedLayout}
            />
          </div>
        </div>
      </div>
    </div>
  );
}